import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Search } from '../models/well/search/search';
import { ISearchResult } from '../models/well/search/search.result';
import { ISelectListItem } from '../models/common/select.list.item';
import { SampleViewModel } from '../models/well/samples/sample.viewmodel';
import { IFileTransfer } from '../models/common/file.transfer';
import { environment } from '@env/environment';
import { ToastrService } from 'ngx-toastr';
import { IWellViewModel } from '../models/well/well.viewmodel';
import { IWellDashboardViewModel } from '../models/well/well-dashboard.viewmodel';
import { ISearchWell } from '../models/well/searchWell';
import { UserService } from './user/user.service';
import { IFuzzySearchResult } from 'app/models/common/fuzzySearchResult';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  }),
  withCredentials: true
};

@Injectable({
  providedIn: 'root',
})
export class OregonWellDataService {
  // #region service

  dashboardCounty: string = "All";
  errorMessages = [];
  retwellService: OregonWellDataService;
  contaminants: ISelectListItem[] = [];
  counties: ISelectListItem[] = [];
  labs: ISelectListItem[] = [];
  labDetails = [];
  methods: ISelectListItem[] = [];
  qa: ISelectListItem[] = [];
  ret: ISelectListItem[] = [];
  units: ISelectListItem[] = [];
  originList: string[] = ["OHA", "GSI Collected", "County Collected", ""];
  decommissionedList: string[] = ["Yes", "No"];

  search: Search = {
    wellId: '',
    sampleId: '',
    contaminant: [],
    address: '',
    county: '',
    city: '',
    state: '',
    zip: '',
    wellTag: '',
    wellLog: '',
    startDate: '',
    endDate: '',
    createStartDate: '',
    createEndDate: '',
    receivedStartDate: '',
    receivedEndDate: '',
    isValidated: false,
    searchBy: 'Well',
    searchResultsMaxRows: '',
    voucherType: '',
    voucherOhaId: '',
    voucherIdentifier: ''
  }
  haveSearchResults = false;
  searchResultsCount = 0;
  searchResults: IFuzzySearchResult[] = [];
  isBrowserIe = /msie\s|trident\//i.test(window.navigator.userAgent);

  // #endregion

  constructor(public userService: UserService, private toastr: ToastrService, private http?: HttpClient) {
    this.lookups();
  }

  get originOptions(): string[] {
    return this.originList;
  }

  get decommissionedOptions(): string[] {
    return this.decommissionedList;
  }


  lookups() {
    if (this.contaminants.length === 0) {
      this.getContaminants().subscribe(result => {
        this.contaminants = result;
      },
        error => {
          this.onHttpError(error);
        }
      );
    }

    if (this.counties.length === 0) {
      this.getCounties().subscribe(result => {
        this.counties = result;
      },
        error => {
          this.onHttpError(error);
        }
      );
    }

    if (this.labs.length === 0) {
      this.getLabs().subscribe(result => {
        this.labs = result;
      },
        error => {
          this.onHttpError(error);
        }
      );
    }

    if (this.labDetails.length === 0) {
      this.getLabDetails().subscribe(result => {
        this.labDetails = result;
      },
        error => {
          this.onHttpError(error);
        }
      );
    }

    if (this.methods.length === 0) {
      this.getMethods().subscribe(result => {
        this.methods = result;
      },
        error => {
          this.onHttpError(error);
        }
      );
    }

    if (this.qa.length === 0) {
      this.getQa().subscribe(result => {
        this.qa = result;
      },
        error => {
          this.onHttpError(error);
        }
      );
    }

    if (this.ret.length === 0) {
      this.getRet().subscribe(result => {
        this.ret = result;
      },
        error => {
          this.onHttpError(error);
        }
      );
    }

    if (this.units.length === 0) {
      this.getUnits().subscribe(result => {
        this.units = result;
      },
        error => {
          this.onHttpError(error);
        }
      );
    }
  }

  // #region well

  wellViewModel: IWellViewModel = {
    id: 0,
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    county: '',
    latitude: '0',
    longitude: '0',
    overrideLatLong: false,
    datum: '',
    coordinateCollectionMethod: '',
    monitoringLocationSourceMapScale: '',
    monitoringLocationDescription: '',
    monitoringLocationComments: '',
    tribalLand: false,
    tribalLandName: '',
    tag: '',
    wellLog: '',
    wellType: '',
    depth: '',
    samplePoint: '',
    monitoringLocationId: '',
    monitoringLocationName: '',
    monitoringLocationType: '',
    formationType: '',
    comment: '',
    township: '',
    range: '',
    section: '',
    taxLot: '',
    dateWellEstablished: '',
    createDate: '',
    lastUpdated: '',
    createdBy: '',
    lastUpdatedBy: '',
    wellsList: [],
    households: [],
    housingUnits: [],
    wellHousingUnitKey: 0,
    decommissioned: false,
    dateDecommissioned: ''
  }

  // #endregion

  // #region actions

  // #region dashboard

  getWellDasboard(county: string): Observable<IWellDashboardViewModel> {
    return this.http.get<IWellDashboardViewModel>(environment.privateApi + 'OregonWellData/welldashboard/' + county, { withCredentials: true });
  }

  // #endregion

  // #region search

  Search(search: Search): Observable<any> {
    return this.http.post(environment.privateApi + 'OregonWellData/Search', search, httpOptions);
  }

  // #endregion

  // #region well

  AddWell(wellViewModel: IWellViewModel): Observable<any> {
    return this.http.post(environment.privateApi + 'OregonWellData/AddWell', wellViewModel, httpOptions);
  }

  GetAddWell(): Observable<any> {
    return this.http.get(environment.privateApi + 'OregonWellData/AddWell', { withCredentials: true });
  }

  EditWell(wellViewModel: IWellViewModel): Observable<any> {
    return this.http.post(environment.privateApi + 'OregonWellData/EditWell', wellViewModel, httpOptions);
  }

  GetEditWell(id): Observable<any> {
    return this.http.get(environment.privateApi + 'OregonWellData/EditWell/' + id, { withCredentials: true });
  }

  SearchWell(searchWell: ISearchWell): Observable<any> {
    return this.http.post(environment.privateApi + 'OregonWellData/Search', searchWell, httpOptions);
  }

  // #endregion

  // #region sample

  AddSample(sampleViewModel: SampleViewModel): Observable<any> {
    return this.http.post(environment.privateApi + 'OregonWellData/AddSample', sampleViewModel, httpOptions);
  }

  GetAddSample(id): Observable<any> {
    return this.http.get(environment.privateApi + 'OregonWellData/AddSample/' + id, { withCredentials: true });
  }

  GetParameterResults(): Observable<any> {
    return this.http.get(environment.privateApi + 'OregonWellData/AddParameterResults', { withCredentials: true });
  }

  GetSampleDelete(id): Observable<any> {
    return this.http.get(environment.privateApi + 'OregonWellData/GetDeleteSample/' + id, { withCredentials: true });
  }

  DeleteSample(id): Observable<any> {
    return this.http.get(environment.privateApi + 'OregonWellData/DeleteSample/' + id, { withCredentials: true });
  }

  // merge
  Merge(id: number, ids: string[]): Observable<any> {
    return this.http.post(environment.privateApi + 'OregonWellData/MergeWells/' + id, ids, httpOptions);
  }

  // csv
  DownloadCsv(search: Search): Observable<any> {
    this.search = search;
    return this.http.post(environment.privateApi + 'OregonWellData/DownloadCsv', search, httpOptions);
  }

  // pdf
  DownloadPdf(html: IFileTransfer): Observable<IFileTransfer> {
    return this.http.post<any>(environment.privateApi + 'OregonWellData/DownloadPdf', html, httpOptions);
  }

  EditSample(sampleViewModel: SampleViewModel): Observable<any> {
    return this.http.post(environment.privateApi + 'OregonWellData/EditSample', sampleViewModel, httpOptions);
  }

  GetEditSample(id): Observable<any> {
    return this.http.get(environment.privateApi + 'OregonWellData/EditSample/' + id, { withCredentials: true });
  }

  // #endregion

  // #endregion

  // #region error

  onHttpError(error: any) {
    console.log('error: ', error);
    this.errorMessages = [];

    Object.keys(error.error.errors).forEach(key => {
      this.errorMessages.push(error.error.errors[key][0]);
    });

    console.log('error details: ' + this.errorMessages);

    return this.errorMessages;
  }

  // #endregion

  // #region lookups

  getContaminants(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/contaminant', { withCredentials: true });
  }

  getCounties(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/county', { withCredentials: true });
  }

  lookupCountyLatLong(lookup): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/lookupCountyLatLong/' + lookup, { withCredentials: true });
  }

  lookupTestResultExceedance(parameterResultId): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/lookupTestResultExceedance/' + parameterResultId, { withCredentials: true });
  }

  getLabs(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/lab' + '', { withCredentials: true });
  }

  getLabDetails(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/labDetails', { withCredentials: true });
  }

  getMethods(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/method', { withCredentials: true });
  }

  getQa(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/qa', { withCredentials: true });
  }

  getRet(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/ret', { withCredentials: true });
  }

  getUnits(): Observable<any> {
    return this.http.get(environment.privateApi + 'Lookups/unit', { withCredentials: true });
  }

  GetMostRecentSampleByVoucherId(id): Observable<any> {
    return this.http.get(environment.privateApi + 'OregonWellData/GetMostRecentSampleByVoucherId/' + id, { withCredentials: true });
  }

  GetHigestNitrateSampleByVoucherId(id): Observable<any> {
    return this.http.get(environment.privateApi + 'OregonWellData/GetHighestNitrateSampleByVoucherId/' + id, { withCredentials: true });
  }

  // #endregion

  // #region date

  isNullOrEmpty(string: string): boolean {
    if (string === "" || string === null) {
      return true;
    }

    return false;
  }

  isOjbectNullOrEmpty(field: any): boolean {
    if (field === null || field === "") {
      return true;
    }

    return false;
  }

  getTodaysDate(subtractYears: number): string {
    let dd: string;
    let mm: string;
    const today = new Date();

    // padStart part of ES7

    // this check is necessary for IE
    // if let unchecked, IE will throw
    // polyfill error on load
    if (!this.isBrowserIe) {
      dd = (<any>String)(today.getDate()).padStart(2, '0');
      mm = (<any>String)(today.getMonth() + 1).padStart(2, '0');
    }
    else {
      dd = (<any>String)(today.getDate());
      mm = (<any>String)(today.getMonth() + 1);
    }

    let yyyy = today.getFullYear();

    if (subtractYears > 0) {
      yyyy = yyyy - subtractYears;
    }

    return mm + '/' + dd + '/' + yyyy;
  }

  // #endregion

  // #region download

  base64toBlob(base64Data, pdf: boolean): Blob {
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);
      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }

    if (pdf) {
      return new Blob(byteArrays, { type: 'application/pdf' });
    }

    return new Blob(byteArrays, { type: 'text/csv' });
  }

  downloadFile(file: string, fileName: string, isPdf: boolean) {
    const data = this.base64toBlob(file, isPdf);
    const nav = (window.navigator as any);
    if (nav.msSaveOrOpenBlob) {
      nav.msSaveOrOpenBlob(data, fileName); //IE & Edge
    }
    else {
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.setAttribute("style", "display: none");
      const url = window.URL.createObjectURL(data);
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }

  stripHtmlComments(html: string): string {
    html = html.replace(/<\!--[\s\S]*?-->/g, "");
    return html;
  }

  // #endregion

  // #region helpers

  showToastSucess(message: any) {
    this.toastr.success(message);
  }

  showToastWarning(message: any) {
    this.toastr.warning(message);
  }

  showToastError(message: any) {
    this.toastr.error(message);
  }

  // #endregion
}